import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Paper,
  Chip,
} from '@mui/material';
import { UploadCloud, X, FileUp } from 'lucide-react';
import { FeatureCredits, formatResetDate } from '../../../utils/types/credits_types';

interface CandiBankUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected: (files: FileList | File[]) => void;
  /** This month's CV credits; drives the "N left" line and the over-limit warning. */
  credits?: FeatureCredits | null;
}

export const CandiBankUploadModal: React.FC<CandiBankUploadModalProps> = ({
  isOpen,
  onClose,
  onFilesSelected,
  credits,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const remaining = credits && !credits.unlimited ? credits.remaining ?? 0 : null;
  const exhausted = remaining !== null && remaining <= 0;

  const submit = (files: FileList | File[]) => onFilesSelected(files);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      submit(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      submit(e.target.files);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'success.main',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 2,
            }}
          >
            <UploadCloud size={20} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
              Upload CVs to Candi Bank
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Add candidate profiles directly without creating a job opening first
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'grey.200' } }}
        >
          <X size={18} />
        </IconButton>
      </DialogTitle>

      {/* Body Dropzone */}
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 3,
              border: '2px dashed',
              borderColor: isDragOver ? 'success.main' : 'grey.300',
              bgcolor: isDragOver ? 'success.50' : 'grey.50',
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'success.main',
                bgcolor: 'success.50',
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: 'success.100',
                color: 'success.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <FileUp size={26} />
            </Box>

            <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
              Drag & Drop multiple CVs here
            </Typography>

            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 360 }}>
              Select single files, multiple CVs, or entire candidate folders. Supports up to 1,000+ files per batch.
            </Typography>

            <Button
              variant="contained"
              color="success"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                px: 2.5,
                py: 1,
              }}
            >
              Browse Files
            </Button>
          </Box>

          {/* Credits for this month */}
          {credits && (
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: exhausted ? 'error.light' : 'success.light',
                bgcolor: exhausted ? 'error.50' : 'success.50',
                color: exhausted ? 'error.dark' : 'success.dark',
              }}
              data-testid="upload-credits"
            >
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {credits.unlimited
                  ? 'Unlimited CV credits'
                  : exhausted
                    ? 'No CV credits left this month'
                    : `${(remaining ?? 0).toLocaleString()} of ${(credits.monthlyLimit ?? 0).toLocaleString()} CV credits left this month`}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {credits.unlimited ? `${credits.used.toLocaleString()} uploaded` : `resets ${formatResetDate(credits)}`}
              </Typography>
            </Paper>
          )}

          {/* Acceptable file types badge footer */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
              borderRadius: 2,
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Supported Formats:
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {['PDF', 'DOCX', 'DOC', 'TXT'].map((ext) => (
                <Chip
                  key={ext}
                  label={ext}
                  size="small"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: '0.625rem',
                    height: 20,
                    bgcolor: 'background.paper',
                    borderColor: 'grey.300',
                  }}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'grey.100', bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 500, mr: 'auto', fontFamily: 'monospace' }}>
          POST /candi-bank/upload
        </Typography>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
